<template>
  <div class="schedule-management">

    <!-- VIP Settings Notification Banners -->
    <div class="vip-settings-alert-container">
      <el-alert
        v-if="!vipEnabled"
        title="🎉 全员免费体验中！当前已由管理员开启全员免 VIP 特权，您可以免费使用包括「一键智能排考」在内的所有高级功能。"
        type="success"
        :closable="false"
        show-icon
        style="margin-bottom: 16px; border-radius: 8px;"
      />
      <el-alert
        v-else-if="!isVipActiveUser"
        title="⚠️ 权限提示：当前系统已开启 VIP 功能限制，您的账号不是 VIP 或已过期，无法使用自动排考功能。请联系管理员开通。"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 16px; border-radius: 8px;"
      />
    </div>

    <!-- Header Card -->
    <el-card class="schedule-header-card">
      <div class="schedule-header">
        <div class="header-left-side">
          <el-button class="back-btn" circle @click="goBack">
            <el-icon><Back /></el-icon>
          </el-button>
          <div class="exam-info">
            <h3>{{ exam?.name || '监考排考' }}</h3>
            <div class="exam-meta">
              <el-tag v-if="exam" type="info" effect="plain" class="meta-tag">
                <el-icon><Calendar /></el-icon>
                {{ exam.sessions?.length || 0 }} 个场次
              </el-tag>
              <el-tag v-if="exam?.grade_rule_type" type="success" effect="plain" class="meta-tag">
                {{ exam.grade_rule_type === 'isolated' ? '年级隔离' : exam.grade_rule_type === 'cross' ? '交叉监考' : exam.grade_rule_type === 'mixed' ? '年级混合' : '自定义分组' }}
              </el-tag>
            </div>
          </div>
        </div>
        
        <div class="header-right-side">
          <!-- Operations Group -->
          <div class="button-group secondary-group">
            <el-button :type="swapMode ? 'danger' : 'default'" @click="toggleSwapMode" plain>
              <el-icon><Switch /></el-icon>
              {{ swapMode ? '退出调换' : '手动调换' }}
            </el-button>
            <el-button type="default" @click="showUnavailableDialog" plain>
              <el-icon><User /></el-icon>
              设置监考限制
            </el-button>
            <el-button type="default" @click="exportToExcel" :loading="exporting" plain>
              <el-icon><Download /></el-icon>
              导出 Excel
            </el-button>

          </div>

          <!-- Divider -->
          <div class="action-divider"></div>

          <!-- Scheduling Group -->
          <div class="scheduling-control">
            <el-popover placement="bottom-end" title="智能排考设置" :width="400" trigger="click" popper-class="schedule-settings-popover">
              <template #reference>
                <el-button class="settings-btn" plain style="margin-right: 12px;">
                  <el-icon><Setting /></el-icon>
                  排考设置
                </el-button>
              </template>
              <div class="settings-popover-content" style="display: flex; flex-direction: column; gap: 0; padding: 4px 0; max-height: 70vh; overflow-y: auto;">
                
                <!-- === 重排策略 === -->
                <div class="setting-section-title">🔒 重排策略</div>
                <div style="display: flex; flex-direction: column; gap: 10px; padding: 8px 0 12px 0; border-bottom: 1px solid #e2e8f0;">
                  <el-tooltip content="开启后，一键智能排考将保留您手动调整好的排考结果（锁定的单元格），只重新分配其余空白单元格" placement="right">
                    <el-checkbox v-model="keepManualOverrides">
                      保留手动微调重排
                    </el-checkbox>
                  </el-tooltip>
                </div>

                <!-- === 工作量均衡 === -->
                <div class="setting-section-title">⚖️ 工作量均衡</div>
                <div style="display: flex; flex-direction: column; gap: 10px; padding: 8px 0 12px 0; border-bottom: 1px solid #e2e8f0;">
                  <el-tooltip content="开启后，算法会在均衡时长的同时，尽可能让老师们的晚场（17:00及以后）监考次数也保持均衡" placement="right">
                    <el-checkbox v-model="balanceLastExams">
                      平衡晚场
                    </el-checkbox>
                  </el-tooltip>
                  
                  <el-tooltip content="开启后，尽量避免安排老师在同一天连续监考两场考试" placement="right">
                    <el-checkbox v-model="avoidConsecutiveInvigilation">
                      不连续监考
                    </el-checkbox>
                  </el-tooltip>

                  <el-tooltip content="开启后，尽量避免安排老师在同一天既监考上午最后一场又监考下午第一场" placement="right">
                    <el-checkbox v-model="avoidMorningLastAfternoonFirst">
                      避免上下午连考
                    </el-checkbox>
                  </el-tooltip>
                </div>

                <!-- === 巡考设置 === -->
                <div class="setting-section-title" style="margin-top: 12px;">👮 巡考设置</div>
                <div style="display: flex; flex-direction: column; gap: 10px; padding: 8px 0 12px 0; border-bottom: 1px solid #e2e8f0;">
                  <el-tooltip content="开启后，算法会自动为每个场次分配一名巡考老师；关闭则不安排任何巡考教师" placement="right">
                    <el-checkbox v-model="patrolNeeded">
                      安排巡考教师
                    </el-checkbox>
                  </el-tooltip>
                  
                  <div v-if="patrolNeeded" style="margin-left: 20px; display: flex; flex-direction: column; gap: 8px;">
                    <div style="font-size: 13px; color: #4b5563;">巡考工作量折算系数:</div>
                    <el-radio-group v-model="patrolRatioPreset" size="small">
                      <el-radio-button value="0.0">不计入 (0%)</el-radio-button>
                      <el-radio-button value="0.5">减半 (50%)</el-radio-button>
                      <el-radio-button value="1.0">同科目时长 (100%)</el-radio-button>
                      <el-radio-button value="custom">自定义比例</el-radio-button>
                    </el-radio-group>
                    <div v-if="patrolRatioPreset === 'custom'" style="display: flex; align-items: center; gap: 8px; margin-top: 4px;">
                      <span style="font-size: 12px; color: #6b7280;">自定义系数:</span>
                      <el-input-number
                        v-model="patrolWorkloadRatio"
                        :min="0"
                        :max="2.0"
                        :step="0.1"
                        :precision="2"
                        size="small"
                        style="width: 100px;"
                      />
                    </div>
                  </div>
                </div>

                <!-- === 学科需求 === -->
                <div class="setting-section-title" style="margin-top: 12px;">📚 学科需求</div>
                <div style="display: flex; flex-direction: column; gap: 10px; padding: 8px 0 12px 0; border-bottom: 1px solid #e2e8f0;">
                  <el-tooltip content="开启后，每场考试只允许教授该科目的教师担任监考（例如语文考试只由语文老师监考）" placement="right">
                    <el-checkbox v-model="subjectRequirementEnabled">
                      仅本学科教师监考
                    </el-checkbox>
                  </el-tooltip>
                  <div v-if="subjectRequirementEnabled" style="margin-left: 20px; display: flex; flex-direction: column; gap: 6px;">
                    <el-radio-group v-model="subjectRequirementType" style="display: flex; gap: 15px;">
                      <el-radio value="all">所有科目</el-radio>
                      <el-radio value="selected">指定科目</el-radio>
                    </el-radio-group>
                    <el-select
                      v-if="subjectRequirementType === 'selected'"
                      v-model="subjectRequirementSubjectIds"
                      multiple
                      collapse-tags
                      :teleported="false"
                      placeholder="选择要求本学科监考的科目"
                      size="small"
                      style="width: 100%;"
                    >
                      <el-option
                        v-for="sub in availableSubjects"
                        :key="sub.id"
                        :label="sub.name"
                        :value="sub.id"
                      />
                    </el-select>
                  </div>
                </div>

                <!-- === 避嫌规则 === -->
                <div class="setting-section-title" style="margin-top: 12px;">🚫 避嫌规则</div>
                <div class="avoidance-settings-section">
                  <el-radio-group v-model="avoidanceRule" class="avoidance-radio-group">
                    <el-radio value="none">不避嫌（默认）</el-radio>
                    <el-radio value="same_grade">同级避嫌（任课老师不监考同年级）</el-radio>
                    <el-radio value="same_subject">同科避嫌（任课老师不监考同科目）</el-radio>
                    <el-radio value="selected_subjects">选择科目避嫌</el-radio>
                  </el-radio-group>
                  <div v-if="avoidanceRule === 'selected_subjects'" style="margin-top: 6px;">
                    <el-select
                      v-model="avoidanceSubjectIds"
                      multiple
                      filterable
                      :teleported="false"
                      placeholder="选择需要避嫌的科目"
                      style="width: 100%;"
                      size="small"
                    >
                      <el-option
                        v-for="subject in availableSubjects"
                        :key="subject.id"
                        :label="subject.name"
                        :value="subject.id"
                      />
                    </el-select>
                  </div>
                  <div style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e2e8f0;">
                    <el-tooltip content="适用于音体美信等跨多个年级任教的公共学科教师。开启后，交叉监考不会因为他们任教多个年级而把他们全部排除。" placement="right">
                      <el-checkbox v-model="publicSubjectGradeAvoidanceExempt">
                        公共学科教师不按年级避嫌
                      </el-checkbox>
                    </el-tooltip>
                    <el-select
                      v-if="publicSubjectGradeAvoidanceExempt"
                      v-model="publicSubjectIds"
                      multiple
                      filterable
                      collapse-tags
                      :teleported="false"
                      placeholder="选择公共学科；不选则按音体美信等默认识别"
                      style="width: 100%; margin-top: 6px;"
                      size="small"
                    >
                      <el-option
                        v-for="subject in availableSubjects"
                        :key="subject.id"
                        :label="subject.name"
                        :value="subject.id"
                      />
                    </el-select>
                    <el-select
                      v-if="publicSubjectGradeAvoidanceExempt"
                      v-model="publicSubjectGrades"
                      multiple
                      filterable
                      collapse-tags
                      :teleported="false"
                      placeholder="选择适用年级；不选则应用于全部年级"
                      style="width: 100%; margin-top: 6px;"
                      size="small"
                    >
                      <el-option
                        v-for="grade in gradeList"
                        :key="grade"
                        :label="grade"
                        :value="grade"
                      />
                    </el-select>
                  </div>
                </div>

                <!-- === 排程优化偏好 === -->
                <div class="setting-section-title" style="margin-top: 12px;">✨ 排程优化偏好</div>
                <div style="display: flex; flex-direction: column; gap: 10px; padding: 8px 0 4px 0;">
                  <el-tooltip content="尽量让教授该科目的教师担任该科目的监考（软约束，工作量允许时优先）" placement="right">
                    <el-checkbox v-model="preferSameSubject">尽量安排同科教师监考</el-checkbox>
                  </el-tooltip>
                  <el-tooltip content="尽量让同年级的教师监考本年级的考试（已默认开启，可关闭以允许跨年级排考）" placement="right">
                    <el-checkbox v-model="preferSameGrade">尽量安排同级教师监考</el-checkbox>
                  </el-tooltip>
                  <el-tooltip content="同时匹配年级和科目时给予最高优先级（比单独同级或同科更强）" placement="right">
                    <el-checkbox v-model="preferSameGradeSubject">尽量安排同级同科教师监考</el-checkbox>
                  </el-tooltip>
                  <el-tooltip content="尽量让同一教师多次监考同一考场（减少换人，保持监考稳定性）" placement="right">
                    <el-checkbox v-model="preferStableTeacher">教师尽量不换</el-checkbox>
                  </el-tooltip>
                  <el-tooltip content="尽量安排教师每天只在上午或只在下午监考，避免一天跨越上下午" placement="right">
                    <el-checkbox v-model="preferHalfDayOnly">教师每天只监考一上午/一下午</el-checkbox>
                  </el-tooltip>
                  <el-tooltip content="若教师需要监考多场，尽量集中在同一天内完成，减少跨天安排" placement="right">
                    <el-checkbox v-model="preferConcentrateDay">多次监考尽量集中在同一天</el-checkbox>
                  </el-tooltip>
                </div>
              </div>
            </el-popover>

            
            <el-button type="primary" @click="autoGenerate" :loading="scheduleStore.generating" class="auto-schedule-btn">
              <el-icon><MagicStick /></el-icon>
              一键智能排考
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- Schedule Grid Tables by Grade -->
    <el-card class="schedule-table-card">
      <!-- Loading Skeleton Screen -->
      <div v-if="loading" class="schedule-skeleton-container">
        <div v-for="i in 2" :key="i" class="skeleton-grade-section">
          <el-skeleton animated>
            <template #template>
              <!-- Grade title skeleton -->
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 15px;">
                <el-skeleton-item variant="rect" style="width: 80px; height: 32px; border-radius: 4px;" />
                <el-skeleton-item variant="text" style="width: 100px; height: 20px;" />
              </div>
              <!-- Grid table skeleton -->
              <div class="schedule-grid" style="margin-bottom: 15px;">
                <table class="exam-schedule-table">
                  <thead>
                    <tr>
                      <th class="fixed-col" style="width: 150px; height: 45px;"><el-skeleton-item variant="text" style="width: 80%;" /></th>
                      <th v-for="j in 4" :key="j" style="height: 45px;"><el-skeleton-item variant="text" style="width: 70%; margin: 0 auto;" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="k in 4" :key="k">
                      <td class="fixed-col" style="padding: 12px;"><el-skeleton-item variant="text" style="width: 60%;" /></td>
                      <td v-for="j in 4" :key="j" style="padding: 12px; text-align: center;">
                        <el-skeleton-item variant="text" style="width: 50%; margin: 0 auto; height: 16px;" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </el-skeleton>
        </div>
      </div>

      <!-- Real Data Content -->
      <div v-else-if="scheduleGrid.length > 0">
        <!-- Loop through grades -->
        <div v-for="grade in gradeList" :key="grade" class="grade-section">
          <h4 class="grade-title">
            <el-tag :type="getGradeTagType(grade)" size="large">{{ grade }}</el-tag>
            监考表
          </h4>

          <div class="schedule-grid">
            <table class="exam-schedule-table">
              <thead>
                <tr>
                  <th rowspan="2" class="fixed-col">试场/时间/科目</th>
                  <!-- 只显示该年级的场次 -->
                  <th v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)" :key="dateIdx"
                    :colspan="dateGroup.sessions.length" class="date-header">
                    {{ formatDate(dateGroup.date) }}
                  </th>
                </tr>
                <tr>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)" :key="'time-'+dateIdx">
                    <th v-for="session in dateGroup.sessions" :key="session.id" class="time-header">
                      <div class="time-slot">{{ session.start_time?.slice(0, 5) }}-{{ session.end_time?.slice(0, 5) }}
                      </div>
                      <div class="subject-name">{{ session.subject_name || session.session_name || '-' }}</div>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="room in getRoomsByGrade(grade)" :key="room.name">
                  <td class="fixed-col room-cell">
                    <div class="room-info">
                      <span class="room-name">{{ room.name }}</span>
                      <span class="room-building">{{ room.building }}</span>
                    </div>
                  </td>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)"
                    :key="'cell-'+dateIdx+'-'+room.name">
                    <td v-for="session in dateGroup.sessions" :key="session.id + '-' + room.name" class="teacher-cell"
                      :class="{ 'swap-active-cell': swapMode }">
                      <div v-for="cell in getScheduleCells(session.id, room)" :key="cell.scheduleId"
                        class="teacher-select-wrapper" :class="{
                          'swap-active': swapMode,
                          'swap-selected': isSelectedForSwap(cell.scheduleId),
                          'manual-override-display': cell.isManual,
                          'editing-cell': isEditingSchedule(cell.scheduleId)
                        }"
                        v-loading="isUpdatingSchedule(cell.scheduleId)"
                        element-loading-text="保存中"
                        @click="handleCellClick(session.id, room, cell.scheduleId)">
                        <el-select v-if="isEditingSchedule(cell.scheduleId)" v-model="cell.teacherId" filterable clearable
                          placeholder="选择教师" size="small"
                          @click.stop
                          @change="(val) => updateTeacher(session.id, room, val, cell.scheduleId)"
                          @visible-change="(visible) => handleEditorVisibleChange(visible, cell.scheduleId)"
                          :class="{ 'manual-override': cell.isManual }">
                          <el-option v-for="t in availableTeachers(session.id)" :key="t.id" :label="t.name"
                            :value="t.id" :disabled="isTeacherBusy(session.id, room.name, t.id, cell.scheduleId)" />
                        </el-select>
                        <button v-else type="button" class="teacher-display-btn" :class="{
                          'is-unassigned': !cell.teacherId,
                          'is-assigned': cell.teacherId && !cell.isManual,
                          'is-manual': cell.isManual
                        }">
                          <el-icon v-if="!cell.teacherId" style="margin-right: 4px; font-size: 11px; color: #9ca3af;"><Plus /></el-icon>
                          {{ getTeacherName(cell.teacherId) || '点击排考' }}
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>
                <!-- 巡考行 -->
                <tr v-if="showPatrolRow" class="patrol-row">
                  <td class="fixed-col room-cell">
                    <div class="room-info">
                      <span class="room-name">巡考</span>
                    </div>
                  </td>
                  <template v-for="(dateGroup, dateIdx) in getSessionsByGrade(grade)"
                    :key="'patrol-'+grade+'-'+dateIdx">
                    <td v-for="session in dateGroup.sessions" :key="'patrol-' + grade + '-' + session.id"
                      class="teacher-cell patrol-cell">
                      <div
                        class="teacher-select-wrapper patrol-wrapper"
                        :class="{
                          'manual-override-display': getPatrolSchedule(session.id)?.is_manual_override,
                          'editing-cell': isEditingSchedule(getPatrolSchedule(session.id)?.id)
                        }"
                        v-loading="isUpdatingSchedule(getPatrolSchedule(session.id)?.id)"
                        element-loading-text="保存中"
                        @click="handlePatrolCellClick(session.id)"
                      >
                        <el-select v-if="isEditingSchedule(getPatrolSchedule(session.id)?.id)"
                          :model-value="getPatrolSchedule(session.id)?.teacher_id" filterable clearable
                          placeholder="选择巡考" size="small" @change="(val) => updatePatrolTeacher(session.id, val)"
                          @click.stop
                          @visible-change="(visible) => handleEditorVisibleChange(visible, getPatrolSchedule(session.id)?.id)"
                          class="patrol-select">
                          <el-option v-for="t in availableTeachers(session.id)" :key="t.id" :label="t.name"
                            :value="t.id" />
                        </el-select>
                         <button v-else type="button" class="teacher-display-btn patrol-display-btn" :class="{
                          'is-unassigned': !getPatrolSchedule(session.id)?.teacher_id,
                          'is-assigned': getPatrolSchedule(session.id)?.teacher_id
                        }">
                          <el-icon v-if="!getPatrolSchedule(session.id)?.teacher_id" style="margin-right: 4px; font-size: 11px; color: #9ca3af;"><Plus /></el-icon>
                          {{ getTeacherName(getPatrolSchedule(session.id)?.teacher_id) || '点击安排' }}
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <el-empty v-else description="暂无排考数据，请先自动排考" />
    </el-card>

    <!-- Workload Stats Card -->
    <el-card class="workload-card" v-if="workloadStats.length > 0">
      <template #header>
        <div class="workload-header-container" style="display: flex; flex-direction: column; gap: 6px; width: 100%;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 600; color: #334155; font-size: 14px;">本次考试监考工作量</span>
            <el-tag type="danger" size="small" effect="plain" class="workload-diff-tag" style="font-weight: 600;">
              最大差距: {{ isMinutes ? (workloadDifference * 60).toFixed(0) + '分钟' : workloadDifference.toFixed(2) + 'h' }}
            </el-tag>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 11px; color: #94a3b8;">（按实际时长，巡考除外）</span>
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-size: 11px; color: #94a3b8;">单位:</span>
              <el-switch
                v-model="isMinutesLocal"
                active-text="分"
                inactive-text="时"
                inline-prompt
                size="small"
                style="--el-switch-on-color: #6366f1; --el-switch-off-color: #94a3b8;"
                @change="handleTimeUnitChange"
              />
            </div>
          </div>
        </div>
      </template>
      <el-table :data="workloadStats" size="small" max-height="300">
        <el-table-column prop="teacher_name" label="教师" width="100" />
        <el-table-column label="工作时长" width="100">
          <template #default="{ row }">
            <el-tag :type="getWorkloadType(row.total_hours)" size="small">{{ isMinutes ? (row.total_hours * 60).toFixed(0) + '分钟' : row.total_hours.toFixed(2) + 'h' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="balanceLastExams" prop="total_last_exams" label="尾场次数" width="80" align="center" />
        <el-table-column label="工作量分布">
          <template #default="{ row }">
            <el-progress :percentage="(row.total_hours / maxWorkloadHours) * 100" :stroke-width="12" :show-text="false"
              :color="getWorkloadColor(row.total_hours)" />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Unavailable Dialog -->
    <el-dialog v-model="unavailableDialogVisible" title="设置监考限制" width="700px">
      <el-alert
        title="提示：修改监考限制后，需要点击【一键智能排考】重新生成排表才会生效"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />
      <el-tabs v-model="unavailableTab">
        <el-tab-pane label="整场考试不监考" name="exam">
          <el-form inline>
            <el-form-item label="选择教师">
              <el-select v-model="selectedUnavailableTeachers" multiple filterable placeholder="选择不参与本次考试的教师"
                style="width: 400px">
                <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="setExamUnavailable">确定</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="examUnavailableList" size="small" v-if="examUnavailableList.length">
            <el-table-column label="教师">
              <template #default="{ row }">
                {{teachers.find(t => t.id === row.teacher_id)?.name || row.teacher_id}}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="danger" @click="removeUnavailable(row.id)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="特定场次不监考" name="session">
          <el-form inline>
            <el-form-item label="选择教师">
              <el-select v-model="sessionUnavailableForm.teacher_id" filterable placeholder="选择教师">
                <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="选择场次">
              <el-select v-model="sessionUnavailableForm.session_ids" multiple placeholder="选择不可监考的场次" style="width: 400px;">
                <el-option v-for="s in exam?.sessions" :key="s.id"
                  :label="`${s.grade || '未分年级'} | ${s.subject_name || s.session_name || '未知科目'} | ${s.exam_date} ${s.start_time?.slice(0, 5)}-${s.end_time?.slice(0, 5)}`" :value="s.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="setSessionUnavailable">确定</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="sessionUnavailableList" size="small" v-if="sessionUnavailableList.length">
            <el-table-column label="教师">
              <template #default="{ row }">
                {{teachers.find(t => t.id === row.teacher_id)?.name || row.teacher_id}}
              </template>
            </el-table-column>
            <el-table-column label="不可监考场次">
              <template #default="{ row }">
                {{ getSessionDisplay(row.exam_session_id) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="danger" @click="removeUnavailable(row.id)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="整场必须监考" name="must">
          <el-form inline>
            <el-form-item label="选择教师">
              <el-select v-model="selectedMustInvigilateTeachers" multiple filterable placeholder="选择必须参与本次考试的教师"
                style="width: 400px">
                <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="setMustInvigilate">确定</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="mustInvigilateList" size="small" v-if="mustInvigilateList.length">
            <el-table-column label="教师">
              <template #default="{ row }">
                {{teachers.find(t => t.id === row.teacher_id)?.name || row.teacher_id}}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="danger" @click="removeMustInvigilate(row.id)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="监考次数" name="countRules">
          <div class="allowed-subjects-panel">
            <div class="allowed-subjects-manual">
              <el-form label-width="100px">
                <el-form-item label="选择教师">
                  <el-select
                    v-model="countRuleForm.teacher_ids"
                    multiple
                    filterable
                    collapse-tags
                    placeholder="选择需要设置次数的教师"
                    style="width: 100%;"
                  >
                    <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
                  </el-select>
                </el-form-item>
                <el-form-item label="最多次数">
                  <el-input-number v-model="countRuleForm.max_sessions" :min="0" :precision="0" placeholder="不限制" />
                </el-form-item>
                <el-form-item label="目标次数">
                  <el-input-number v-model="countRuleForm.target_sessions" :min="0" :precision="0" placeholder="不设置" />
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="saveCountRulesManual">保存设置</el-button>
                  <el-button @click="clearCountRuleForm">清空输入</el-button>
                </el-form-item>
              </el-form>
            </div>

            <el-table :data="countRuleRows" size="small" border max-height="220" empty-text="未设置则表示该教师不限制次数，也不指定目标次数">
              <el-table-column label="教师" width="120">
                <template #default="{ row }">
                  {{ getTeacherNameById(row.teacher_id) }}
                </template>
              </el-table-column>
              <el-table-column label="最多次数" width="120" align="center">
                <template #default="{ row }">
                  {{ row.max_sessions ?? '不限制' }}
                </template>
              </el-table-column>
              <el-table-column label="目标次数" width="120" align="center">
                <template #default="{ row }">
                  {{ row.target_sessions ?? '不设置' }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" align="center">
                <template #default="{ row }">
                  <el-button text type="danger" @click="clearTeacherCountRule(row.teacher_id)">清空</el-button>
                </template>
              </el-table-column>
            </el-table>

            <el-tabs v-model="countRuleImportTab" class="allowed-subject-import-tabs">
              <el-tab-pane label="Excel 导入" name="excel">
                <div class="allowed-subject-import-box">
                  <el-button type="primary" plain @click="downloadCountRuleTemplate">
                    <el-icon><Download /></el-icon>
                    下载模板
                  </el-button>
                  <el-upload
                    ref="countRuleUploadRef"
                    action=""
                    :auto-upload="false"
                    :limit="1"
                    accept=".xlsx,.xls"
                    :on-change="handleCountRuleExcelChange"
                    :on-remove="handleCountRuleExcelRemove"
                    style="margin-top: 12px;"
                  >
                    <el-button>选择 Excel 文件</el-button>
                    <template #tip>
                      <div class="el-upload__tip">列名支持：教师姓名、最多监考次数、目标监考次数（不需要工号）；只填目标次数时，最多次数可留空</div>
                    </template>
                  </el-upload>
                  <el-button type="primary" :disabled="!countRuleExcelFile" @click="submitCountRuleExcel">
                    导入 Excel
                  </el-button>
                </div>
              </el-tab-pane>
              <el-tab-pane label="粘贴导入" name="text">
                <div class="allowed-subject-import-box">
                  <p class="allowed-subject-hint">每行一位教师，支持：教师姓名 目标次数；或：教师姓名 最多次数 目标次数。示例：张老师 1 / 李老师 3 2</p>
                  <el-input
                    v-model="countRuleImportText"
                    type="textarea"
                    :rows="5"
                    placeholder="张老师 1&#10;李老师 3 2"
                    @input="parseCountRuleText"
                  />
                  <el-table v-if="parsedCountRuleItems.length" :data="parsedCountRuleItems" size="small" border max-height="180" style="margin-top: 8px;">
                    <el-table-column prop="teacher_name" label="教师" width="120" />
                    <el-table-column prop="max_sessions" label="最多次数" width="100" align="center" />
                    <el-table-column prop="target_sessions" label="目标次数" width="100" align="center" />
                  </el-table>
                  <el-button type="primary" :disabled="parsedCountRuleItems.length === 0" @click="submitCountRuleText" style="margin-top: 8px;">
                    导入粘贴内容
                  </el-button>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </el-tab-pane>

        <el-tab-pane label="可监考科目" name="allowedSubjects">
          <div class="allowed-subjects-panel">
            <div class="allowed-subjects-manual">
              <el-form label-width="90px">
                <el-form-item label="选择教师">
                  <el-select
                    v-model="allowedSubjectForm.teacher_ids"
                    multiple
                    filterable
                    collapse-tags
                    placeholder="选择需要设置的教师"
                    style="width: 100%;"
                  >
                    <el-option v-for="t in teachers" :key="t.id" :label="t.name" :value="t.id" />
                  </el-select>
                </el-form-item>
                <el-form-item label="可监考科目">
                  <el-select
                    v-model="allowedSubjectForm.subject_ids"
                    multiple
                    filterable
                    collapse-tags
                    placeholder="选择这些教师可监考的科目"
                    style="width: 100%;"
                  >
                    <el-option v-for="s in availableSubjects" :key="s.id" :label="s.name" :value="s.id" />
                  </el-select>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="saveAllowedSubjectsManual">保存设置</el-button>
                  <el-button @click="clearAllowedSubjectForm">清空输入</el-button>
                </el-form-item>
              </el-form>
            </div>

            <el-table :data="allowedSubjectRows" size="small" border max-height="220" empty-text="未设置则表示该教师不限制监考科目">
              <el-table-column label="教师" width="120">
                <template #default="{ row }">
                  {{ getTeacherNameById(row.teacher_id) }}
                </template>
              </el-table-column>
              <el-table-column label="可监考科目">
                <template #default="{ row }">
                  <el-tag v-for="subject in row.subjects" :key="subject.id" size="small" style="margin-right: 4px; margin-bottom: 4px;">
                    {{ subject.name }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" align="center">
                <template #default="{ row }">
                  <el-button text type="danger" @click="clearTeacherAllowedSubjects(row.teacher_id)">清空</el-button>
                </template>
              </el-table-column>
            </el-table>

            <el-tabs v-model="allowedSubjectImportTab" class="allowed-subject-import-tabs">
              <el-tab-pane label="Excel 导入" name="excel">
                <div class="allowed-subject-import-box">
                  <el-button type="primary" plain @click="downloadAllowedSubjectTemplate">
                    <el-icon><Download /></el-icon>
                    下载模板
                  </el-button>
                  <el-radio-group v-model="allowedSubjectImportMode" size="small" style="margin-left: 12px;">
                    <el-radio-button value="replace">覆盖同名教师</el-radio-button>
                    <el-radio-button value="append">追加科目</el-radio-button>
                  </el-radio-group>
                  <el-upload
                    ref="allowedSubjectUploadRef"
                    action=""
                    :auto-upload="false"
                    :limit="1"
                    accept=".xlsx,.xls"
                    :on-change="handleAllowedSubjectExcelChange"
                    :on-remove="handleAllowedSubjectExcelRemove"
                    style="margin-top: 12px;"
                  >
                    <el-button>选择 Excel 文件</el-button>
                    <template #tip>
                      <div class="el-upload__tip">列名支持：教师姓名、可监考科目（不需要工号）；多个科目建议用顿号“、”分隔，也支持中英文逗号、空格等</div>
                    </template>
                  </el-upload>
                  <el-button type="primary" :disabled="!allowedSubjectExcelFile" @click="submitAllowedSubjectExcel">
                    导入 Excel
                  </el-button>
                </div>
              </el-tab-pane>
              <el-tab-pane label="粘贴导入" name="text">
                <div class="allowed-subject-import-box">
                  <p class="allowed-subject-hint">每行一位教师，格式：教师姓名 可监考科目。多个科目建议用顿号“、”分隔，也支持中英文逗号、空格等。示例：张老师 语文、地理、生物</p>
                  <el-input
                    v-model="allowedSubjectImportText"
                    type="textarea"
                    :rows="5"
                    placeholder="张老师 语文、地理、生物&#10;李老师 数学,政治,历史"
                    @input="parseAllowedSubjectText"
                  />
                  <el-table v-if="parsedAllowedSubjectItems.length" :data="parsedAllowedSubjectItems" size="small" border max-height="180" style="margin-top: 8px;">
                    <el-table-column prop="teacher_name" label="教师" width="120" />
                    <el-table-column label="可监考科目">
                      <template #default="{ row }">
                        {{ row.subject_names.join('、') }}
                      </template>
                    </el-table-column>
                  </el-table>
                  <el-button type="primary" :disabled="parsedAllowedSubjectItems.length === 0" @click="submitAllowedSubjectText" style="margin-top: 8px;">
                    导入粘贴内容
                  </el-button>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- Newcomer Welcome & Guide Dialog -->
    <el-dialog
      v-model="guideDialogVisible"
      title="🎉 欢迎使用爱教务智能教务系统"
      width="480px"
      align-center
      class="welcome-dialog"
      :close-on-click-modal="false"
    >
      <div class="welcome-dialog-content" style="text-align: center; padding: 10px 0;">
        <div class="welcome-illustration" style="font-size: 64px; margin-bottom: 16px;">🎓</div>
        <h3 style="margin: 0 0 10px 0; font-size: 18px; color: #1e293b;">开启高效的排考体验</h3>
        <p style="margin: 0 0 20px 0; font-size: 14px; color: #64748b; line-height: 1.6;">
          检测到您是首次进入排监考模块，我们为您准备了一份美观的<b>操作指南教程</b>，帮助您快速了解基础数据准备、智能规则设置和一键自动排考。
        </p>
      </div>
      <template #footer>
        <div class="dialog-footer" style="display: flex; gap: 12px; justify-content: center;">
          <el-button @click="dismissGuide">暂不需要</el-button>
          <el-button type="primary" @click="goToTutorial">立即查看教程</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Back, MagicStick, User, Switch, Download, Calendar, Plus, Setting } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { examApi, scheduleApi, teacherApi, authApi } from '@/api'
import { useScheduleStore } from '@/stores/schedule'
import { isVip, getUser, setUser } from '@/utils/auth'

const isMinutes = computed(() => getUser()?.use_minutes_format || false)
const isMinutesLocal = ref(getUser()?.use_minutes_format || false)

const handleTimeUnitChange = async (val) => {
  try {
    const payload = {
      use_minutes_format: val
    }
    const updatedUser = await authApi.updateProfile(payload)
    setUser(updatedUser)
    ElMessage.success(`显示单位已切换为 ${val ? '分钟' : '小时'}`)
    window.location.reload()
  } catch (e) {
    console.error(e)
    ElMessage.error('切换时间单位失败')
    isMinutesLocal.value = !val
  }
}

const scheduleStore = useScheduleStore()

const router = useRouter()
const route = useRoute()
const examId = computed(() => Number(route.params.examId))

const exam = ref(null)
const teachers = ref([])
const schedules = ref([])
const unavailableList = ref([])
const mustInvigilateList = ref([])
const allowedSubjectRows = ref([])
const countRuleRows = ref([])
const loading = ref(false)
const exporting = ref(false)
const balanceLastExams = ref(false)
const avoidConsecutiveInvigilation = ref(false)
const avoidMorningLastAfternoonFirst = ref(false)
const keepManualOverrides = ref(false)
let isInitialLoad = true

const vipEnabled = ref(true)
const isVipActiveUser = computed(() => isVip())

const guideDialogVisible = ref(false)
const allowedSubjectForm = ref({ teacher_ids: [], subject_ids: [] })
const allowedSubjectImportTab = ref('excel')
const allowedSubjectImportMode = ref('replace')
const allowedSubjectExcelFile = ref(null)
const allowedSubjectUploadRef = ref(null)
const allowedSubjectImportText = ref('')
const parsedAllowedSubjectItems = ref([])
const countRuleForm = ref({ teacher_ids: [], max_sessions: null, target_sessions: null })
const countRuleImportTab = ref('excel')
const countRuleExcelFile = ref(null)
const countRuleUploadRef = ref(null)
const countRuleImportText = ref('')
const parsedCountRuleItems = ref([])

const dismissGuide = () => {
  localStorage.setItem('tutorial_dismissed', 'true')
  guideDialogVisible.value = false
}

const goToTutorial = () => {
  localStorage.setItem('tutorial_dismissed', 'true')
  guideDialogVisible.value = false
  router.push('/tutorial')
}

const fetchVipSettings = async () => {
  try {
    const res = await authApi.getSettings()
    vipEnabled.value = res.vip_enabled
  } catch (e) {
    console.error('Failed to load system settings:', e)
  }
}

watch(balanceLastExams, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) {
    return
  }
  try {
    await examApi.update(examId.value, { balance_last_exams: newVal })
    ElMessage.success('选项已自动保存')
  } catch (e) {
    console.error(e)
    ElMessage.error('保存选项失败')
  }
})

watch(avoidConsecutiveInvigilation, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) {
    return
  }
  try {
    await examApi.update(examId.value, { avoid_consecutive_invigilation: newVal })
    ElMessage.success('选项已自动保存')
  } catch (e) {
    console.error(e)
    ElMessage.error('保存选项失败')
  }
})

watch(avoidMorningLastAfternoonFirst, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) {
    return
  }
  try {
    await examApi.update(examId.value, { avoid_morning_last_afternoon_first: newVal })
    ElMessage.success('选项已自动保存')
  } catch (e) {
    console.error(e)
    ElMessage.error('保存选项失败')
  }
})

// ===== 新增规则：学科需求 / 避嫌 / 优化偏好 =====
const patrolNeeded = ref(true)
const patrolWorkloadRatio = ref(0.0)
const patrolRatioPreset = ref('0.0')

watch(patrolRatioPreset, (newVal) => {
  if (newVal !== 'custom') {
    patrolWorkloadRatio.value = parseFloat(newVal)
  }
})

watch(patrolWorkloadRatio, (newVal) => {
  if ([0.0, 0.5, 1.0].includes(newVal)) {
    patrolRatioPreset.value = String(newVal.toFixed(1))
  } else {
    patrolRatioPreset.value = 'custom'
  }
})

const subjectRequirementEnabled = ref(false)
const subjectRequirementType = ref('all')
const subjectRequirementSubjectIds = ref([])
const avoidanceRule = ref('none')
const avoidanceSubjectIds = ref([])
const publicSubjectGradeAvoidanceExempt = ref(true)
const publicSubjectIds = ref([])
const publicSubjectGrades = ref([])
const preferSameSubject = ref(false)
const preferSameGrade = ref(true)
const preferSameGradeSubject = ref(false)
const preferStableTeacher = ref(false)
const preferHalfDayOnly = ref(false)
const preferConcentrateDay = ref(false)

// 科目列表（用于避嫌科目选择器）
const availableSubjects = ref([])

const fetchSubjects = async () => {
  try {
    const subjectMap = {}

    try {
      const subjects = await teacherApi.getSubjects()
      subjects.forEach(s => {
        if (s.id && s.name) {
          subjectMap[s.id] = { id: s.id, name: s.name }
        }
      })
    } catch (e) {
      console.warn('Failed to load global subjects, using exam sessions instead:', e)
    }

    // Include subjects already used by this exam as a fallback and to preserve old selections.
    (exam.value?.sessions || []).forEach(s => {
      if (s.subject_id && s.subject_name) {
        subjectMap[s.subject_id] = { id: s.subject_id, name: s.subject_name }
      }
    })
    availableSubjects.value = Object.values(subjectMap)
  } catch (e) {
    console.error('Failed to load subjects:', e)
  }
}

// 自动保存 watcher（新增字段）
const makeAutoSaveWatcher = (field, apiField) => {
  watch(field, async (newVal) => {
    if (isInitialLoad || !examId.value || isNaN(examId.value)) return
    try {
      await examApi.update(examId.value, { [apiField]: newVal })
    } catch (e) {
      console.error(e)
    }
  })
}

makeAutoSaveWatcher(patrolNeeded, 'patrol_needed')
makeAutoSaveWatcher(patrolWorkloadRatio, 'patrol_workload_ratio')
makeAutoSaveWatcher(subjectRequirementEnabled, 'subject_requirement_enabled')
makeAutoSaveWatcher(avoidanceRule, 'avoidance_rule')
makeAutoSaveWatcher(publicSubjectGradeAvoidanceExempt, 'public_subject_grade_avoidance_exempt')
makeAutoSaveWatcher(preferSameSubject, 'prefer_same_subject')
makeAutoSaveWatcher(preferSameGrade, 'prefer_same_grade')
makeAutoSaveWatcher(preferSameGradeSubject, 'prefer_same_grade_subject')
makeAutoSaveWatcher(preferStableTeacher, 'prefer_stable_teacher')
makeAutoSaveWatcher(preferHalfDayOnly, 'prefer_half_day_only')
makeAutoSaveWatcher(preferConcentrateDay, 'prefer_concentrate_day')

watch(avoidanceSubjectIds, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) return
  try {
    await examApi.update(examId.value, { avoidance_subjects: JSON.stringify(newVal) })
  } catch (e) {
    console.error(e)
  }
})

watch(publicSubjectIds, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) return
  try {
    await examApi.update(examId.value, { public_subjects: newVal.length ? JSON.stringify(newVal) : null })
  } catch (e) {
    console.error(e)
  }
})

watch(publicSubjectGrades, async (newVal) => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) return
  try {
    await examApi.update(examId.value, { public_subject_grades: newVal.length ? JSON.stringify(newVal) : null })
  } catch (e) {
    console.error(e)
  }
})

watch([subjectRequirementType, subjectRequirementSubjectIds], async () => {
  if (isInitialLoad || !examId.value || isNaN(examId.value)) return
  try {
    const val = subjectRequirementType.value === 'selected' 
      ? JSON.stringify(subjectRequirementSubjectIds.value) 
      : null
    await examApi.update(examId.value, { subject_requirement_subjects: val })
  } catch (e) {
    console.error(e)
  }
})

// Swap Mode
const swapMode = ref(false)
const selectedSwapCell = ref(null) // { sessionId, roomName, scheduleId }
const editingScheduleId = ref(null)
const updatingScheduleIds = ref(new Set())

// Unavailable dialog
const unavailableDialogVisible = ref(false)
const unavailableTab = ref('exam')
const selectedUnavailableTeachers = ref([])
const sessionUnavailableForm = ref({ teacher_id: null, session_ids: [] })
const selectedMustInvigilateTeachers = ref([])

// Schedule cell map for quick access
const scheduleCellMap = ref({})

// Note: roomList was moved to getRoomsByGrade function

// Computed: Get unique grades from exam sessions (not schedules)
// 新模型：年级信息在场次上
const gradeList = computed(() => {
  if (!exam.value?.sessions) return []

  const grades = new Set()
  exam.value.sessions.forEach(s => {
    grades.add(s.grade || '未分年级')
  })

  if (grades.size === 0) return []

  return Array.from(grades).sort((a, b) => {
    if (a === '未分年级') return 1;
    if (b === '未分年级') return -1;
    return a.localeCompare(b, 'zh', { numeric: true })
  })
})

// Get rooms for a specific grade (from sessions that have that grade)
// 新模型：从该年级的场次中收集考场
const getRoomsByGrade = (grade) => {
  if (!exam.value?.sessions) return []

  const normalizedGrade = grade === '未分年级' ? null : grade
  const gradeSessionIds = new Set(
    exam.value.sessions
      .filter(s => (s.grade || null) === normalizedGrade)
      .map(s => s.id)
  )

  // 从排考中找出属于这些场次的考场（去重）
  const rooms = new Map()
  schedules.value.forEach(s => {
    if (gradeSessionIds.has(s.exam_session_id) && s.room_name && s.role !== '巡考') {
      const key = `${s.building || ''}_${s.room_name}`
      if (!rooms.has(key)) {
        rooms.set(key, {
          id: s.exam_room_id,
          name: s.room_name,
          building: s.building || ''
        })
      }
    }
  })

  return Array.from(rooms.values()).sort((a, b) => {
    const buildA = a.building || ''
    const buildB = b.building || ''
    const buildCompare = buildA.localeCompare(buildB, 'zh', { numeric: true })
    if (buildCompare !== 0) {
      return buildCompare
    }
    const nameA = a.name || ''
    const nameB = b.name || ''
    return nameA.localeCompare(nameB, 'zh', { numeric: true })
  })
}

// Get sessions for a specific grade, grouped by date
// 新模型：只返回该年级的场次
const getSessionsByGrade = (grade) => {
  if (!exam.value?.sessions) return []

  const normalizedGrade = grade === '未分年级' ? null : grade
  const gradeSessions = exam.value.sessions
    .filter(s => (s.grade || null) === normalizedGrade)
    .sort((a, b) => {
      // 先按日期排序，再按时间排序
      const dateCompare = a.exam_date.localeCompare(b.exam_date)
      if (dateCompare !== 0) return dateCompare
      return a.start_time.localeCompare(b.start_time)
    })

  // 按日期分组
  const groups = {}
  gradeSessions.forEach(s => {
    if (!groups[s.exam_date]) {
      groups[s.exam_date] = { date: s.exam_date, sessions: [] }
    }
    groups[s.exam_date].sessions.push(s)
  })

  return Object.values(groups).sort((a, b) => a.date.localeCompare(b.date))
}

// Grade tag type
const getGradeTagType = (grade) => {
  if (!grade || grade === '未分年级') return 'info'
  const types = ['success', 'warning', 'danger', 'primary', 'info']
  const idx = gradeList.value.indexOf(grade)
  if (idx !== -1) {
    return types[idx % types.length]
  }
  let hash = 0
  for (let i = 0; i < grade.length; i++) {
    hash = grade.charCodeAt(i) + ((hash << 5) - hash)
  }
  return types[Math.abs(hash) % types.length]
}

// Note: Unified time slots removed - each grade shows only its own sessions

// Build schedule grid for display
const scheduleGrid = computed(() => schedules.value)

const showPatrolRow = computed(() => {
  if (patrolNeeded.value) return true
  return schedules.value.some(s => s.role === '巡考')
})

// Workload stats from backend
const workloadStats = ref([])

const maxWorkloadHours = computed(() => {
  return Math.max(...workloadStats.value.map(w => w.total_hours), 1)
})

const workloadDifference = computed(() => {
  if (workloadStats.value.length === 0) return 0
  const hours = workloadStats.value.map(w => w.total_hours)
  return Math.max(...hours) - Math.min(...hours)
})

// Filter unavailable lists
const examUnavailableList = computed(() =>
  unavailableList.value.filter(u => !u.exam_session_id)
)

const sessionUnavailableList = computed(() =>
  unavailableList.value.filter(u => u.exam_session_id)
)

// Build cell map for quick access
// 使用 session_id + room_name 作为 key，因为同一物理考场在不同场次有不同的 exam_room_id
watch(schedules, (newSchedules) => {
  const map = {}
  newSchedules.forEach(s => {
    if (s.role !== '巡考') {
      const key = `${s.exam_session_id}-${s.room_name}`
      if (!map[key]) {
        map[key] = []
      }
      map[key].push({
        scheduleId: s.id,
        teacherId: s.teacher_id,
        isManual: s.is_manual_override,
        examRoomId: s.exam_room_id
      })
    }
  })
  // Sort them so they display consistently
  Object.values(map).forEach(slots => {
    slots.sort((a, b) => a.scheduleId - b.scheduleId)
  })
  scheduleCellMap.value = map
}, { immediate: true })

// Get cell data - 使用 room_name 匹配
const getScheduleCells = (sessionId, room) => {
  const key = `${sessionId}-${room.name}`
  return scheduleCellMap.value[key] || []
}

const getTeacherName = (teacherId) => {
  if (!teacherId) return ''
  return teachers.value.find(t => t.id === teacherId)?.name || ''
}

const isEditingSchedule = (scheduleId) => {
  return Boolean(scheduleId) && editingScheduleId.value === scheduleId
}

const isUpdatingSchedule = (scheduleId) => {
  return Boolean(scheduleId) && updatingScheduleIds.value.has(scheduleId)
}

const setScheduleUpdating = (scheduleId, isUpdating) => {
  if (!scheduleId) return
  const next = new Set(updatingScheduleIds.value)
  if (isUpdating) {
    next.add(scheduleId)
  } else {
    next.delete(scheduleId)
  }
  updatingScheduleIds.value = next
}

const openScheduleEditor = (scheduleId) => {
  if (!scheduleId || swapMode.value || isUpdatingSchedule(scheduleId)) return
  editingScheduleId.value = scheduleId
}

const handleEditorVisibleChange = (visible, scheduleId) => {
  if (!visible && editingScheduleId.value === scheduleId && !isUpdatingSchedule(scheduleId)) {
    editingScheduleId.value = null
  }
}

// Check if teacher is already assigned in this session
const isTeacherBusy = (sessionId, currentRoomName, teacherId, scheduleId) => {
  return schedules.value.some(s =>
    s.exam_session_id === sessionId &&
    s.teacher_id === teacherId &&
    s.id !== scheduleId &&
    s.role !== '巡考'
  )
}

// Get available teachers for a session (excluding unavailable ones)
const availableTeachers = (sessionId) => {
  const unavailableTeacherIds = new Set(
    unavailableList.value
      .filter(u => !u.exam_session_id || u.exam_session_id === sessionId)
      .map(u => u.teacher_id)
  )
  return teachers.value.filter(t => !unavailableTeacherIds.has(t.id))
}

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return `${d.getMonth() + 1}.${d.getDate()}（${weekdays[d.getDay()]}）`
}

// Note: getSessionSubject removed - using session.subject_name directly in template

// Get session display text
const getSessionDisplay = (sessionId) => {
  const session = exam.value?.sessions?.find(s => s.id === sessionId)
  if (!session) return sessionId
  const gradeStr = session.grade ? `${session.grade} | ` : '未分年级 | '
  const subjectStr = (session.subject_name || session.session_name) ? `${session.subject_name || session.session_name} | ` : ''
  return `${gradeStr}${subjectStr}${session.exam_date} ${session.start_time?.slice(0, 5)}-${session.end_time?.slice(0, 5)}`
}

// Note: getPatrolTeacher removed - using getPatrolSchedule instead

// Get patrol schedule object for editing
const getPatrolSchedule = (sessionId) => {
  return schedules.value.find(s =>
    s.exam_session_id === sessionId && s.role === '巡考'
  ) || null
}

const handlePatrolCellClick = (sessionId) => {
  const patrol = getPatrolSchedule(sessionId)
  if (!patrol) {
    ElMessage.warning('该场次没有巡考记录')
    return
  }
  openScheduleEditor(patrol.id)
}

// Update patrol teacher
const updatePatrolTeacher = async (sessionId, teacherId) => {
  const patrol = getPatrolSchedule(sessionId)
  if (!patrol) {
    ElMessage.warning('该场次没有巡考记录')
    return
  }

  const applyPatrolLocal = async (tid) => {
    // Update local data
    patrol.teacher_id = tid
    patrol.teacher_name = teachers.value.find(t => t.id === tid)?.name || ''
    patrol.is_manual_override = true

    // Refresh workload stats
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  }

  try {
    setScheduleUpdating(patrol.id, true)
    await scheduleApi.update(patrol.id, { teacher_id: teacherId })
    ElMessage.success('巡考老师已更新')
    await applyPatrolLocal(teacherId)
  } catch (e) {
    const detail = e.response?.data?.detail
    if (detail && typeof detail === 'object' && detail.code === 'CONSTRAINT_VIOLATION') {
      try {
        await ElMessageBox.confirm(
          `${detail.message}，是否强行覆盖并保存？`,
          '约束规则警告',
          {
            confirmButtonText: '强行保存',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        setScheduleUpdating(patrol.id, true)
        await scheduleApi.update(patrol.id, { teacher_id: teacherId }, true)
        ElMessage.success('强行保存巡考老师成功')
        await applyPatrolLocal(teacherId)
      } catch (confirmErr) {
        if (confirmErr !== 'cancel') {
          console.error(confirmErr)
        }
        fetchSchedules()
      }
    } else {
      console.error(e)
      fetchSchedules()
    }
  } finally {
    setScheduleUpdating(patrol.id, false)
    if (editingScheduleId.value === patrol.id) {
      editingScheduleId.value = null
    }
  }
}

// Workload color based on hours
const getWorkloadColor = (hours) => {
  const avg = workloadStats.value.length > 0
    ? workloadStats.value.reduce((sum, w) => sum + w.total_hours, 0) / workloadStats.value.length
    : 0
  if (hours > avg * 1.3) return '#f56c6c'
  if (hours < avg * 0.7) return '#e6a23c'
  return '#67c23a'
}

const getWorkloadType = (hours) => {
  const avg = workloadStats.value.length > 0
    ? workloadStats.value.reduce((sum, w) => sum + w.total_hours, 0) / workloadStats.value.length
    : 0
  if (hours > avg * 1.3) return 'danger'
  if (hours < avg * 0.7) return 'warning'
  return 'success'
}

// Fetch data
const fetchExam = async () => {
  if (!examId.value || isNaN(examId.value)) return
  try {
    exam.value = await examApi.getById(examId.value)
    if (exam.value) {
      if (exam.value.balance_last_exams !== undefined) {
        balanceLastExams.value = exam.value.balance_last_exams
      }
      if (exam.value.avoid_consecutive_invigilation !== undefined) {
        avoidConsecutiveInvigilation.value = exam.value.avoid_consecutive_invigilation
      }
      if (exam.value.avoid_morning_last_afternoon_first !== undefined) {
        avoidMorningLastAfternoonFirst.value = exam.value.avoid_morning_last_afternoon_first
      }
      // ===== 恢复新增规则设置 =====
      if (exam.value.patrol_needed !== undefined) {
        patrolNeeded.value = exam.value.patrol_needed
      }
      if (exam.value.patrol_workload_ratio !== undefined) {
        patrolWorkloadRatio.value = exam.value.patrol_workload_ratio
      }
      if (exam.value.subject_requirement_enabled !== undefined) {
        subjectRequirementEnabled.value = exam.value.subject_requirement_enabled
      }
      if (exam.value.subject_requirement_subjects) {
        try {
          const parsed = JSON.parse(exam.value.subject_requirement_subjects) || []
          if (parsed && parsed.length > 0) {
            subjectRequirementType.value = 'selected'
            subjectRequirementSubjectIds.value = parsed
          } else {
            subjectRequirementType.value = 'all'
            subjectRequirementSubjectIds.value = []
          }
        } catch (e) {
          subjectRequirementType.value = 'all'
          subjectRequirementSubjectIds.value = []
        }
      } else {
        subjectRequirementType.value = 'all'
        subjectRequirementSubjectIds.value = []
      }
      if (exam.value.avoidance_rule !== undefined) {
        avoidanceRule.value = exam.value.avoidance_rule || 'none'
      }
      if (exam.value.avoidance_subjects) {
        try {
          avoidanceSubjectIds.value = JSON.parse(exam.value.avoidance_subjects) || []
        } catch (e) {
          avoidanceSubjectIds.value = []
        }
      }
      if (exam.value.public_subject_grade_avoidance_exempt !== undefined) {
        publicSubjectGradeAvoidanceExempt.value = exam.value.public_subject_grade_avoidance_exempt
      }
      if (exam.value.public_subjects) {
        try {
          publicSubjectIds.value = JSON.parse(exam.value.public_subjects) || []
        } catch (e) {
          publicSubjectIds.value = []
        }
      } else {
        publicSubjectIds.value = []
      }
      if (exam.value.public_subject_grades) {
        try {
          publicSubjectGrades.value = JSON.parse(exam.value.public_subject_grades) || []
        } catch (e) {
          publicSubjectGrades.value = []
        }
      } else {
        publicSubjectGrades.value = []
      }
      if (exam.value.prefer_same_subject !== undefined) preferSameSubject.value = exam.value.prefer_same_subject
      if (exam.value.prefer_same_grade !== undefined) preferSameGrade.value = exam.value.prefer_same_grade
      if (exam.value.prefer_same_grade_subject !== undefined) preferSameGradeSubject.value = exam.value.prefer_same_grade_subject
      if (exam.value.prefer_stable_teacher !== undefined) preferStableTeacher.value = exam.value.prefer_stable_teacher
      if (exam.value.prefer_half_day_only !== undefined) preferHalfDayOnly.value = exam.value.prefer_half_day_only
      if (exam.value.prefer_concentrate_day !== undefined) preferConcentrateDay.value = exam.value.prefer_concentrate_day
    }
    // Set timeout to prevent watcher from triggering API during initial load
    setTimeout(() => { isInitialLoad = false }, 100)
    // 加载科目列表
    fetchSubjects()
  } catch (e) {
    console.error(e)
  }
}

const fetchTeachers = async () => {
  try {
    teachers.value = await teacherApi.getAll()
  } catch (e) {
    console.error(e)
  }
}

const fetchSchedules = async () => {
  if (!examId.value || isNaN(examId.value)) return
  loading.value = true
  try {
    schedules.value = await scheduleApi.getAll(examId.value)
    // Also fetch workload stats for this exam
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const fetchUnavailable = async () => {
  if (!examId.value || isNaN(examId.value)) return
  try {
    let allUnavailable = []
    let allMust = []
    for (const teacher of teachers.value) {
      const unavailable = await teacherApi.getUnavailable(teacher.id, examId.value)
      allUnavailable = allUnavailable.concat(unavailable)
      
      const must = await teacherApi.getMustInvigilate(teacher.id, examId.value)
      allMust = allMust.concat(must)
    }
    unavailableList.value = allUnavailable
    mustInvigilateList.value = allMust
    allowedSubjectRows.value = await teacherApi.getAllowedSubjects(examId.value)
    countRuleRows.value = await teacherApi.getCountRules(examId.value)
  } catch (e) {
    console.error(e)
  }
}

const getTeacherNameById = (teacherId) => {
  return teachers.value.find(t => t.id === teacherId)?.name || teacherId
}

const clearCountRuleForm = () => {
  countRuleForm.value = { teacher_ids: [], max_sessions: null, target_sessions: null }
}

const saveCountRulesManual = async () => {
  if (countRuleForm.value.teacher_ids.length === 0) {
    ElMessage.warning('请选择教师')
    return
  }
  if (countRuleForm.value.max_sessions == null && countRuleForm.value.target_sessions == null) {
    ElMessage.warning('请至少设置最多监考次数或目标监考次数')
    return
  }
  try {
    await teacherApi.saveCountRules({
      exam_id: examId.value,
      items: countRuleForm.value.teacher_ids.map(teacherId => ({
        teacher_id: teacherId,
        max_sessions: countRuleForm.value.max_sessions,
        target_sessions: countRuleForm.value.target_sessions
      }))
    })
    ElMessage.success('监考次数规则已保存')
    clearCountRuleForm()
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('保存监考次数规则失败')
  }
}

const clearTeacherCountRule = async (teacherId) => {
  try {
    await teacherApi.clearCountRule(examId.value, teacherId)
    ElMessage.success('已清空该教师的监考次数规则')
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('清空失败')
  }
}

const getTeacherByNameOrEmployee = (teacherName, employeeId) => {
  const name = String(teacherName || '').trim()
  const emp = String(employeeId || '').trim()
  if (name) {
    const byEmployeeFromName = teachers.value.find(t => String(t.employee_id || '').trim() === name)
    if (byEmployeeFromName) return byEmployeeFromName
  }
  if (emp) {
    const byEmployee = teachers.value.find(t => String(t.employee_id || '').trim() === emp)
    if (byEmployee) return byEmployee
  }
  if (name) {
    return teachers.value.find(t => t.name === name || t.short_name === name)
  }
  return null
}

const parseOptionalCount = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isInteger(n) && n >= 0 ? n : null
}

const parseCountRuleText = () => {
  const lines = countRuleImportText.value.split('\n')
  const result = []
  const warnings = []

  lines.forEach((line, index) => {
    const raw = line.trim()
    if (!raw) return
    const parts = raw.includes('\t')
      ? raw.split('\t').map(p => p.trim()).filter(Boolean)
      : raw.split(/\s+/).map(p => p.trim()).filter(Boolean)

    if (parts.length < 2) {
      warnings.push(`第 ${index + 1} 行：格式不完整`)
      return
    }

    const teacher = getTeacherByNameOrEmployee(parts[0], parts[1])
    if (!teacher) {
      warnings.push(`第 ${index + 1} 行：未找到教师 ${parts[0]}`)
      return
    }

    const secondTokenIsEmployee = parts[1] && String(teacher.employee_id || '').trim() === parts[1]
    const countParts = secondTokenIsEmployee ? parts.slice(2) : parts.slice(1)
    const maxSessions = countParts.length >= 2 ? parseOptionalCount(countParts[0]) : null
    const targetSessions = countParts.length >= 2
      ? parseOptionalCount(countParts[1])
      : parseOptionalCount(countParts[0])

    if (maxSessions == null && targetSessions == null) {
      warnings.push(`第 ${index + 1} 行：请填写目标次数，或填写最多次数+目标次数`)
      return
    }

    result.push({
      teacher_id: teacher.id,
      teacher_name: teacher.name,
      max_sessions: maxSessions,
      target_sessions: targetSessions
    })
  })

  parsedCountRuleItems.value = result
  if (warnings.length) {
    ElMessage.warning(warnings.slice(0, 3).join('；'))
  }
}

const submitCountRuleText = async () => {
  if (parsedCountRuleItems.value.length === 0) return
  try {
    await teacherApi.saveCountRules({
      exam_id: examId.value,
      items: parsedCountRuleItems.value.map(item => ({
        teacher_id: item.teacher_id,
        max_sessions: item.max_sessions,
        target_sessions: item.target_sessions
      }))
    })
    ElMessage.success(`成功导入 ${parsedCountRuleItems.value.length} 位教师的监考次数规则`)
    countRuleImportText.value = ''
    parsedCountRuleItems.value = []
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入粘贴内容失败')
  }
}

const handleCountRuleExcelChange = (uploadFile) => {
  countRuleExcelFile.value = uploadFile.raw
}

const handleCountRuleExcelRemove = () => {
  countRuleExcelFile.value = null
}

const downloadCountRuleTemplate = async () => {
  try {
    const blob = await teacherApi.downloadCountRulesTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '监考次数规则导入模板.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    ElMessage.error('下载模板失败')
  }
}

const submitCountRuleExcel = async () => {
  if (!countRuleExcelFile.value) {
    ElMessage.warning('请选择 Excel 文件')
    return
  }
  try {
    const result = await teacherApi.importCountRulesExcel(examId.value, countRuleExcelFile.value)
    const errorHint = result.errors?.length ? `，${result.errors.length} 条未导入` : ''
    ElMessage.success((result.message || '导入成功') + errorHint)
    countRuleExcelFile.value = null
    countRuleUploadRef.value?.clearFiles()
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('Excel 导入失败')
  }
}

const clearAllowedSubjectForm = () => {
  allowedSubjectForm.value = { teacher_ids: [], subject_ids: [] }
}

const saveAllowedSubjectsManual = async () => {
  if (allowedSubjectForm.value.teacher_ids.length === 0) {
    ElMessage.warning('请选择教师')
    return
  }
  if (allowedSubjectForm.value.subject_ids.length === 0) {
    ElMessage.warning('请选择可监考科目')
    return
  }
  try {
    await teacherApi.saveAllowedSubjects({
      exam_id: examId.value,
      mode: 'replace',
      items: allowedSubjectForm.value.teacher_ids.map(teacherId => ({
        teacher_id: teacherId,
        subject_ids: allowedSubjectForm.value.subject_ids
      }))
    })
    ElMessage.success('可监考科目已保存')
    clearAllowedSubjectForm()
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('保存可监考科目失败')
  }
}

const clearTeacherAllowedSubjects = async (teacherId) => {
  try {
    await teacherApi.clearAllowedSubjects(examId.value, teacherId)
    ElMessage.success('已清空该教师的可监考科目限制')
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('清空失败')
  }
}

const splitImportValues = (text) => {
  return String(text || '').split(/[\s,，、/|;；]+/).map(s => s.trim()).filter(Boolean)
}

const parseAllowedSubjectText = () => {
  const lines = allowedSubjectImportText.value.split('\n')
  const subjectByName = new Map(availableSubjects.value.map(s => [s.name, s]))
  const teacherByName = new Map(teachers.value.map(t => [t.name, t]))
  const result = []
  const warnings = []

  lines.forEach((line, index) => {
    const raw = line.trim()
    if (!raw) return
    let teacherName = ''
    let subjectsRaw = ''
    if (raw.includes('\t')) {
      const parts = raw.split('\t').map(p => p.trim()).filter(Boolean)
      teacherName = parts[0] || ''
      subjectsRaw = parts.slice(1).join('、')
    } else if (/\s+/.test(raw)) {
      const spaceParts = raw.split(/\s+/).map(p => p.trim()).filter(Boolean)
      teacherName = spaceParts[0] || ''
      subjectsRaw = spaceParts.slice(1).join('、')
    } else {
      const parts = raw.split(/[，,;；]/).map(p => p.trim()).filter(Boolean)
      if (parts.length >= 2) {
        teacherName = parts[0]
        subjectsRaw = parts.slice(1).join('、')
      } else {
        teacherName = ''
        subjectsRaw = ''
      }
    }

    const teacher = teacherByName.get(teacherName)
    if (!teacher) {
      warnings.push(`第 ${index + 1} 行：未找到教师 ${teacherName || raw}`)
      return
    }

    const subjectNames = splitImportValues(subjectsRaw)
    const subjectIds = []
    const validSubjectNames = []
    subjectNames.forEach(name => {
      const subject = subjectByName.get(name)
      if (subject) {
        subjectIds.push(subject.id)
        validSubjectNames.push(subject.name)
      } else {
        warnings.push(`第 ${index + 1} 行：未找到科目 ${name}`)
      }
    })
    if (subjectIds.length === 0) return
    result.push({
      teacher_id: teacher.id,
      teacher_name: teacher.name,
      subject_ids: Array.from(new Set(subjectIds)),
      subject_names: Array.from(new Set(validSubjectNames))
    })
  })

  parsedAllowedSubjectItems.value = result
  if (warnings.length) {
    ElMessage.warning(warnings.slice(0, 3).join('；'))
  }
}

const submitAllowedSubjectText = async () => {
  if (parsedAllowedSubjectItems.value.length === 0) return
  try {
    await teacherApi.saveAllowedSubjects({
      exam_id: examId.value,
      mode: 'replace',
      items: parsedAllowedSubjectItems.value.map(item => ({
        teacher_id: item.teacher_id,
        subject_ids: item.subject_ids
      }))
    })
    ElMessage.success(`成功导入 ${parsedAllowedSubjectItems.value.length} 位教师的可监考科目`)
    allowedSubjectImportText.value = ''
    parsedAllowedSubjectItems.value = []
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入粘贴内容失败')
  }
}

const handleAllowedSubjectExcelChange = (uploadFile) => {
  allowedSubjectExcelFile.value = uploadFile.raw
}

const handleAllowedSubjectExcelRemove = () => {
  allowedSubjectExcelFile.value = null
}

const downloadAllowedSubjectTemplate = async () => {
  try {
    const blob = await teacherApi.downloadAllowedSubjectsTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '可监考科目导入模板.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    ElMessage.error('下载模板失败')
  }
}

const submitAllowedSubjectExcel = async () => {
  if (!allowedSubjectExcelFile.value) {
    ElMessage.warning('请选择 Excel 文件')
    return
  }
  try {
    const result = await teacherApi.importAllowedSubjectsExcel(
      examId.value,
      allowedSubjectImportMode.value,
      allowedSubjectExcelFile.value
    )
    const errorHint = result.errors?.length ? `，${result.errors.length} 条未导入` : ''
    ElMessage.success((result.message || '导入成功') + errorHint)
    allowedSubjectExcelFile.value = null
    allowedSubjectUploadRef.value?.clearFiles()
    await fetchSubjects()
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('Excel 导入失败')
  }
}

const setMustInvigilate = async () => {
  if (selectedMustInvigilateTeachers.value.length === 0) {
    ElMessage.warning('请选择教师')
    return
  }
  
  try {
    for (const teacherId of selectedMustInvigilateTeachers.value) {
      await teacherApi.setMustInvigilate({
        teacher_id: teacherId,
        exam_id: examId.value
      })
    }
    ElMessage.success('设置成功')
    selectedMustInvigilateTeachers.value = []
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('设置失败')
  }
}

const removeMustInvigilate = async (id) => {
  try {
    await teacherApi.removeMustInvigilate(id)
    ElMessage.success('移除成功')
    await fetchUnavailable()
  } catch (e) {
    console.error(e)
    ElMessage.error('移除失败')
  }
}

// Update teacher assignment
const updateTeacher = async (sessionId, room, teacherId, scheduleId) => {
  if (!scheduleId) {
    ElMessage.warning('该单元格没有排考记录')
    return
  }

  const applyUpdateLocal = async (tid) => {
    // Update local data
    const schedule = schedules.value.find(s => s.id === scheduleId)
    if (schedule) {
      schedule.teacher_id = tid
      schedule.teacher_name = teachers.value.find(t => t.id === tid)?.name || ''
      schedule.is_manual_override = true
    }

    // Rebuild cell map (we update the specific array item)
    const key = `${sessionId}-${room.name}`
    if (scheduleCellMap.value[key]) {
      const slot = scheduleCellMap.value[key].find(c => c.scheduleId === scheduleId)
      if (slot) {
        slot.teacherId = tid
        slot.isManual = true
      }
    }

    // Refresh workload stats
    workloadStats.value = await scheduleApi.getExamStats(examId.value)
  }

  try {
    setScheduleUpdating(scheduleId, true)
    await scheduleApi.update(scheduleId, { teacher_id: teacherId })
    ElMessage.success('已保存')
    await applyUpdateLocal(teacherId)
  } catch (e) {
    const detail = e.response?.data?.detail
    if (detail && typeof detail === 'object' && detail.code === 'CONSTRAINT_VIOLATION') {
      try {
        await ElMessageBox.confirm(
          `${detail.message}，是否强行覆盖并保存？`,
          '约束规则警告',
          {
            confirmButtonText: '强行保存',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        setScheduleUpdating(scheduleId, true)
        await scheduleApi.update(scheduleId, { teacher_id: teacherId }, true)
        ElMessage.success('强行保存成功')
        await applyUpdateLocal(teacherId)
      } catch (confirmErr) {
        if (confirmErr !== 'cancel') {
          console.error(confirmErr)
        }
        fetchSchedules()
      }
    } else {
      console.error(e)
      fetchSchedules()
    }
  } finally {
    setScheduleUpdating(scheduleId, false)
    if (editingScheduleId.value === scheduleId) {
      editingScheduleId.value = null
    }
  }
}

// Toggle Swap Mode
const toggleSwapMode = () => {
  swapMode.value = !swapMode.value
  selectedSwapCell.value = null
  editingScheduleId.value = null
  if (swapMode.value) {
    ElMessage.info('调换模式已开启，请依次点击两个监考单元格进行互换')
  }
}

// Handle Cell Click (for Swap Mode)
const handleCellClick = async (sessionId, room, scheduleId) => {
  if (!scheduleId) return

  if (!swapMode.value) {
    openScheduleEditor(scheduleId)
    return
  }

  if (!selectedSwapCell.value) {
    // Select first cell
    selectedSwapCell.value = {
      sessionId,
      roomName: room.name,
      scheduleId: scheduleId
    }
  } else {
    // Select second cell and swap
    const first = selectedSwapCell.value
    const second = {
      sessionId,
      roomName: room.name,
      scheduleId: scheduleId
    }

    if (first.scheduleId === second.scheduleId) {
      selectedSwapCell.value = null
      return
    }

    try {
      loading.value = true
      await scheduleApi.swap(first.scheduleId, second.scheduleId)
      ElMessage.success('调换成功')
      await fetchSchedules()
    } catch (e) {
      const detail = e.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'CONSTRAINT_VIOLATION') {
        try {
          await ElMessageBox.confirm(
            `${detail.message}，是否强行调换？`,
            '约束规则警告',
            {
              confirmButtonText: '强行调换',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
          loading.value = true
          await scheduleApi.swap(first.scheduleId, second.scheduleId, true)
          ElMessage.success('强行调换成功')
          await fetchSchedules()
        } catch (confirmErr) {
          if (confirmErr !== 'cancel') {
            console.error(confirmErr)
          }
        }
      } else {
        console.error(e)
      }
    } finally {
      loading.value = false
      selectedSwapCell.value = null
    }
  }
}

const isSelectedForSwap = (scheduleId) => {
  return selectedSwapCell.value?.scheduleId === scheduleId
}

// Export to Excel
const exportToExcel = async () => {
  exporting.value = true
  try {
    const response = await examApi.exportExcel(examId.value)

    // Create a link and trigger download
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = url

    // Try to get filename from content-disposition if possible, or use default
    const filename = `监考表_${exam.value?.name || examId.value}_${new Date().toLocaleDateString()}.xlsx`

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    ElMessage.success('导出成功')
  } catch (e) {
    console.error('Export failed:', e)
    ElMessage.error('导出失败，请重试')
  } finally {
    exporting.value = false
  }
}

// Auto generate
const autoGenerate = async () => {
  if (vipEnabled.value && !isVip()) {
    ElMessageBox.alert('您当前不是VIP或VIP已过期，无法使用自动排考功能。请联系管理员开通。', '权限提示', {
      confirmButtonText: '确定',
      type: 'warning'
    })
    return
  }
  try {
    const confirmMessage = keepManualOverrides.value
      ? '自动排考将保留您手动微调的结果（锁定单元格），只重新分配其余空白单元格，是否继续？'
      : '自动排考将清除现有排考并重新生成，是否继续？'
      
    await ElMessageBox.confirm(
      confirmMessage,
      '确认自动排考',
      { type: 'warning' }
    )

    scheduleStore.startGenerating(examId.value)

    // 模拟进度更新
    const progressInterval = setInterval(() => {
      if (scheduleStore.progressPercent < 30) {
        scheduleStore.updateProgress('正在加载教师和考场信息...', scheduleStore.progressPercent + 5)
      } else if (scheduleStore.progressPercent < 50) {
        scheduleStore.updateProgress('正在分配巡考老师...', scheduleStore.progressPercent + 3)
      } else if (scheduleStore.progressPercent < 80) {
        scheduleStore.updateProgress('正在优化监考老师分配...', scheduleStore.progressPercent + 2)
      } else if (scheduleStore.progressPercent < 95) {
        scheduleStore.updateProgress('正在平衡工作量...', scheduleStore.progressPercent + 1)
      }
    }, 300)

    const result = await scheduleApi.autoGenerate(
      examId.value,
      balanceLastExams.value,
      avoidConsecutiveInvigilation.value,
      avoidMorningLastAfternoonFirst.value,
      {
        keep_manual_overrides: keepManualOverrides.value,
        subject_requirement_enabled: subjectRequirementEnabled.value,
        subject_requirement_subject_ids: subjectRequirementType.value === 'selected' ? subjectRequirementSubjectIds.value : [],
        patrol_needed: patrolNeeded.value,
        patrol_workload_ratio: patrolWorkloadRatio.value,
        avoidance_rule: avoidanceRule.value,
        avoidance_subject_ids: avoidanceSubjectIds.value,
        public_subject_grade_avoidance_exempt: publicSubjectGradeAvoidanceExempt.value,
        public_subject_ids: publicSubjectIds.value,
        public_subject_grades: publicSubjectGrades.value,
        prefer_same_subject: preferSameSubject.value,
        prefer_same_grade: preferSameGrade.value,
        prefer_same_grade_subject: preferSameGradeSubject.value,
        prefer_stable_teacher: preferStableTeacher.value,
        prefer_half_day_only: preferHalfDayOnly.value,
        prefer_concentrate_day: preferConcentrateDay.value,
      }
    )

    clearInterval(progressInterval)
    scheduleStore.updateProgress('排考完成！', 100)

    await new Promise(resolve => setTimeout(resolve, 500))

    if (result.success) {
      ElMessage.success(result.message || '排考成功')
    } else {
      if (result.message && result.message.includes('排考前置检查失败')) {
        ElMessageBox.alert(result.message, '数据配置不完整', {
          confirmButtonText: '我知道了',
          type: 'warning',
          customStyle: 'max-width: 600px;'
        })
      } else {
        ElMessage.warning({
          message: result.message || '排考未完全成功，请检查约束条件',
          duration: 5000,
          showClose: true
        })
      }
    }
    fetchSchedules()
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      // 捕捉后端返回的 API 错误内容并以警告形式展示
      const errorMsg = e.response?.data?.detail || '排考启动失败'
      ElMessageBox.alert(errorMsg, '排考失败', {
        confirmButtonText: '确定',
        type: 'error',
        customStyle: 'max-width: 600px;'
      })
    }
  } finally {
    scheduleStore.finishGenerating()
  }
}

// Watch for global scheduling completion to refresh data if we are on the same exam page
watch(() => scheduleStore.generating, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    if (scheduleStore.currentExamId === examId.value) {
      fetchSchedules()
    }
  }
})

const goBack = () => {
  router.push('/exams')
}


// Unavailable management
const showUnavailableDialog = () => {
  unavailableDialogVisible.value = true
  fetchUnavailable()
}

const setExamUnavailable = async () => {
  if (!selectedUnavailableTeachers.value.length) {
    ElMessage.warning('请选择教师')
    return
  }
  try {
    for (const teacherId of selectedUnavailableTeachers.value) {
      await teacherApi.setUnavailable({
        teacher_id: teacherId,
        exam_id: examId.value,
        exam_session_id: null
      })
    }
    ElMessage.success('已设置')
    selectedUnavailableTeachers.value = []
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

const setSessionUnavailable = async () => {
  const { teacher_id, session_ids } = sessionUnavailableForm.value
  if (!teacher_id || !session_ids.length) {
    ElMessage.warning('请选择教师和场次')
    return
  }
  try {
    for (const sessionId of session_ids) {
      await teacherApi.setUnavailable({
        teacher_id,
        exam_id: examId.value,
        exam_session_id: sessionId
      })
    }
    ElMessage.success('已设置')
    sessionUnavailableForm.value = { teacher_id: null, session_ids: [] }
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

const removeUnavailable = async (id) => {
  try {
    await teacherApi.removeUnavailable(id)
    ElMessage.success('已移除')
    fetchUnavailable()
  } catch (e) {
    console.error(e)
  }
}

onMounted(async () => {
  await Promise.all([fetchExam(), fetchTeachers(), fetchVipSettings()])
  await fetchSchedules()
  fetchUnavailable()
  
  // Show guide dialog for first-time users
  if (!localStorage.getItem('tutorial_dismissed')) {
    guideDialogVisible.value = true
  }
})
</script>

<style scoped>
.schedule-management {
  max-width: 1800px;
  position: relative;
}

.scheduling-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scheduling-progress {
  background: white;
  padding: 40px 60px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.scheduling-progress h3 {
  margin: 15px 0 10px;
  color: #303133;
}

.scheduling-progress .progress-step {
  color: #409eff;
  font-weight: 500;
  margin: 10px 0;
}

.scheduling-progress .progress-hint {
  color: #909399;
  font-size: 12px;
  margin-top: 15px;
}

.schedule-header-card {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.header-left-side {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  transition: all 0.2s;
}

.back-btn:hover {
  background-color: #f3f4f6;
  transform: translateX(-2px);
}

.exam-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.exam-info h3 {
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}

.exam-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-tag {
  font-size: 12px;
  border-radius: 6px;
}

.meta-tag :deep(.el-tag__content) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.header-right-side {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.button-group {
  display: flex;
  gap: 8px;
}

.action-divider {
  width: 1px;
  height: 24px;
  background-color: #e5e7eb;
}

.scheduling-control {
  display: flex;
  align-items: center;
  gap: 16px;
}

.balance-checkbox {
  margin: 0 !important;
}

.auto-schedule-btn {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%) !important;
  border: none !important;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
  transition: all 0.3s !important;
}

.auto-schedule-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.3);
}

@media (max-width: 1024px) {
  .schedule-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-right-side {
    width: 100%;
    justify-content: space-between;
  }
  
  .action-divider {
    display: none;
  }
}

.schedule-table-card {
  margin-bottom: 20px;
  overflow-x: auto;
}

.schedule-grid {
  overflow-x: auto;
}

.exam-schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.exam-schedule-table th,
.exam-schedule-table td {
  border: 1px solid #e5e7eb;
  padding: 8px;
  text-align: center;
}

.exam-schedule-table th {
  background: #f3f4f6;
  color: #374151;
  font-weight: 500;
}

.fixed-col {
  position: sticky;
  left: 0;
  background: #ffffff !important;
  z-index: 10;
  min-width: 120px;
}

.date-header {
  background: #e0e7ff !important;
  color: #4338ca !important;
  font-size: 14px;
}

.time-header {
  min-width: 100px;
}

.time-slot {
  font-size: 12px;
  color: #6b7280;
}

.subject-name {
  color: #1f2937;
  font-weight: 600;
}

.room-cell {
  text-align: left !important;
}

.room-info {
  display: flex;
  flex-direction: column;
}

.room-name {
  font-weight: 600;
  color: #1f2937;
}

.room-building {
  font-size: 11px;
  color: #6b7280;
}

.teacher-cell {
  padding: 4px !important;
  min-width: 100px;
  transition: all 0.3s;
}

.teacher-select-wrapper {
  margin-bottom: 4px;
  border-radius: 4px;
  min-height: 30px;
  position: relative;
}

.teacher-select-wrapper:last-child {
  margin-bottom: 0;
}

.teacher-select-wrapper.swap-active {
  cursor: pointer;
  padding: 2px;
}

.teacher-select-wrapper:not(.swap-active) {
  cursor: pointer;
}

.teacher-select-wrapper.swap-active:hover {
  background-color: rgba(64, 158, 255, 0.1);
}

.teacher-select-wrapper.swap-selected {
  background-color: rgba(64, 158, 255, 0.2) !important;
  outline: 2px solid #409eff;
  outline-offset: -2px;
}

.teacher-select-wrapper.editing-cell {
  cursor: default;
}

.teacher-display-btn {
  width: 100%;
  min-height: 28px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  line-height: 18px;
  padding: 4px 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.teacher-display-btn:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.teacher-display-btn.is-assigned {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
  font-weight: 500;
}

.teacher-display-btn.is-assigned:hover {
  background: #dbeafe;
  border-color: #bfdbfe;
  color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(30, 64, 175, 0.08);
}

.teacher-display-btn.is-manual {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fef3c7;
  font-weight: 500;
}

.teacher-display-btn.is-manual:hover {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(180, 83, 9, 0.08);
}

.teacher-display-btn.is-unassigned {
  background: #f8fafc;
  color: #94a3b8;
  border: 1px dashed #cbd5e1;
}

.teacher-display-btn.is-unassigned:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #64748b;
  transform: translateY(-1px);
}

.patrol-display-btn.is-assigned {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #dcfce7;
}

.patrol-display-btn.is-assigned:hover {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #15803d;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(22, 101, 52, 0.08);
}



.teacher-cell.no-session-cell {
  background: #f3f4f6;
}

.empty-cell {
  text-align: center;
  color: #d1d5db;
  font-size: 12px;
}

.time-header.no-session {
  background: #f3f4f6;
  color: #9ca3af;
}

.teacher-select-wrapper :deep(.el-select) {
  width: 100%;
}

.teacher-select-wrapper :deep(.el-select__wrapper) {
  background: #f9fafb;
  min-height: 28px;
}

.teacher-select-wrapper :deep(.manual-override .el-select__wrapper) {
  background: rgba(251, 191, 36, 0.15);
  border-color: rgba(251, 191, 36, 0.5);
}

.teacher-select-wrapper :deep(.el-loading-mask) {
  border-radius: 4px;
}

.teacher-select-wrapper :deep(.el-loading-spinner) {
  margin-top: -10px;
}

.teacher-select-wrapper :deep(.el-loading-text) {
  font-size: 11px;
  margin-top: 2px;
}

.patrol-row {
  background: #f9fafb;
}

.patrol-cell {
  color: #6b7280;
}

.workload-card {
  max-width: 500px;
}

.workload-rule {
  font-size: 11px;
  color: #a0aec0;
  font-weight: normal;
}

.workload-card :deep(.el-card__header) {
  padding: 12px 16px;
}

.workload-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.workload-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workload-diff-tag {
  font-weight: bold;
}

.grade-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.grade-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.grade-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 16px;
}

/* 排考设置 Popover 分区标题 */
.setting-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #4f46e5;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0 0 4px 0;
  border-bottom: 2px solid #eef2ff;
}

.avoidance-settings-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.avoidance-radio-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.avoidance-radio-group :deep(.el-radio) {
  justify-content: flex-start;
  width: 100%;
  margin-right: 0;
}

</style>
